import { useMemo } from 'react';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

function fmtMoney(n) { return `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }

// 14-week velocity series for one SKU. Returns up to 14 weekly unit_sold
// values, oldest → newest. Pure derivation from already-loaded weeklyData;
// no extra fetch.
function velocityFor(weeklyData, skuId) {
  const rows = weeklyData
    .filter(w => w.sku_id === skuId)
    .sort((a, b) => a.week_start.localeCompare(b.week_start)); // ascending
  return rows.slice(-14).map(w => Number(w.units_sold || 0));
}

function Sparkline({ data, width = 72, height = 22, stroke = '#6E7278' }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ flex: '0 0 auto' }} aria-hidden>
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function StockDashboard({
  skus = [], catalog = [], weeklyData = [], incomingData = [],
  onNavigate, onOpenSku,
}) {

  const catMap = useMemo(() => {
    const m = {};
    catalog.forEach(c => { m[c.id] = c; });
    return m;
  }, [catalog]);

  const skuMetrics = useMemo(() => skus.map(s => {
    const cat = s.catalog_id ? catMap[s.catalog_id] : null;
    const unitCost = cat?.unit_cost ? Number(cat.unit_cost) : 0;
    const soh = Number(s.stock_on_hand || 0);
    const monthlyAvg = Number(s.monthly_avg_override || s.monthly_sales_avg || 0);
    const dailyAvg = monthlyAvg / 30;
    const daysOfCover = dailyAvg > 0 ? Math.round(soh / dailyAvg) : soh > 0 ? 999 : 0;
    const sohValue = soh * unitCost;

    const incoming = incomingData.filter(i => i.sku_id === s.id);
    const incomingQty = incoming.reduce((sum, i) => sum + Number(i.qty || 0), 0);
    const incomingValue = incomingQty * unitCost;
    const nextArrival = incoming.length > 0 ? incoming[0].expected_date : null;

    return {
      ...s, unitCost, soh, monthlyAvg, dailyAvg, daysOfCover, sohValue,
      incomingQty, incomingValue, nextArrival,
      catName: cat?.sku_name || s.sku_name,
      catCode: cat?.sku_code || '',
    };
  }), [skus, catMap, incomingData]);

  const totalSohValue = useMemo(() => skuMetrics.reduce((s, m) => s + m.sohValue, 0), [skuMetrics]);
  const totalUnits = useMemo(() => skuMetrics.reduce((s, m) => s + m.soh, 0), [skuMetrics]);
  const totalIncomingValue = useMemo(() => skuMetrics.reduce((s, m) => s + m.incomingValue, 0), [skuMetrics]);
  const totalIncomingShipments = incomingData.length;
  const nextLanding = incomingData.length > 0 ? incomingData[0].expected_date : null;
  const activeSkuCount = skus.length;

  const avgDaysOfCover = useMemo(() => {
    const totalDaily = skuMetrics.reduce((s, m) => s + m.dailyAvg, 0);
    if (totalDaily <= 0) return 0;
    return Math.round(skuMetrics.reduce((s, m) => s + m.soh, 0) / totalDaily);
  }, [skuMetrics]);

  const lowestCover = useMemo(() => {
    const selling = skuMetrics.filter(m => m.dailyAvg > 0);
    if (selling.length === 0) return null;
    return selling.reduce((min, m) => m.daysOfCover < min.daysOfCover ? m : min, selling[0]);
  }, [skuMetrics]);

  const alerts = useMemo(() => {
    const list = [];
    skuMetrics.forEach(m => {
      if (m.dailyAvg > 0 && m.daysOfCover < 30) {
        const hasIncoming = m.incomingQty > 0;
        list.push({
          id: m.id, type: 'stockout', priority: m.daysOfCover < 14 ? 'red' : 'yellow',
          sku: m.catName,
          text: hasIncoming
            ? `${m.daysOfCover} days cover. ${m.incomingQty} units arriving ${m.nextArrival || 'soon'}.`
            : `${m.daysOfCover} days cover. No incoming shipments. Lead time: ${m.lead_time_days || 84} days.`,
        });
      }
      if (m.soh === 0 && m.dailyAvg > 0) {
        list.push({
          id: m.id + '-oos', type: 'stockout', priority: 'red',
          sku: m.catName, text: 'Out of stock. Selling ~' + Math.round(m.monthlyAvg) + ' units/month.',
        });
      }
    });
    return list.sort((a, b) => (a.priority === 'red' ? 0 : 1) - (b.priority === 'red' ? 0 : 1));
  }, [skuMetrics]);

  // Top 4 most-at-risk SKUs (lowest days of cover, only those actually selling).
  const needsAttention = useMemo(() =>
    [...skuMetrics]
      .filter(m => m.dailyAvg > 0)
      .sort((a, b) => a.daysOfCover - b.daysOfCover)
      .slice(0, 4)
  , [skuMetrics]);

  const daysToNext = nextLanding ? Math.max(0, Math.ceil((new Date(nextLanding).getTime() - Date.now()) / 86400000)) : null;

  // Tone helpers — keep colour intent consistent across blocks.
  const coverTone = (d) => d < 14 ? '#C2410C' : d < 30 ? '#B05610' : d < 60 ? '#0F8E6F' : '#16A34A';

  // Click to open the On-Hand drawer for that SKU. If no handler is wired,
  // fall back to navigating to the On Hand tab.
  function openSku(id) {
    if (onOpenSku) onOpenSku(id);
    else if (onNavigate) onNavigate('onhand');
  }

  return (
    <div style={{ padding: '24px 40px 48px', fontFamily: SANS }}>

      {/* ── Action banner ────────────────────────────────────────── */}
      <ActionBanner alerts={alerts} />

      {/* ── KPI strip ────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 24, marginBottom: 32 }}>
        <KpiCard
          label="Stock on hand"
          value={fmtMoney(totalSohValue)}
          sub={`${totalUnits.toLocaleString()} units · ${activeSkuCount} SKUs`}
        />
        <KpiCard
          label="Days of cover"
          value={`${avgDaysOfCover}d`}
          sub={lowestCover ? `Lowest: ${lowestCover.catName} at ${lowestCover.daysOfCover}d` : 'No active sales data'}
          valueColor={avgDaysOfCover < 30 ? '#C2410C' : '#0F1419'}
        />
        <KpiCard
          label="Incoming"
          value={fmtMoney(totalIncomingValue)}
          sub={totalIncomingShipments > 0
            ? `${totalIncomingShipments} shipment${totalIncomingShipments > 1 ? 's' : ''}${daysToNext != null ? `, next in ${daysToNext}d` : ''}`
            : 'No incoming shipments'}
        />
        <KpiCard
          label="Active SKUs"
          value={String(activeSkuCount)}
          sub={`${catalog.length} in catalogue`}
        />
      </div>

      {/* ── Needs attention cards ────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontFamily: SANS, fontSize: 16, fontWeight: 500, color: '#0F1419', margin: 0 }}>
            Needs attention
          </h2>
          <div style={{ fontSize: 12, color: '#6E7278' }}>Sorted by days of cover</div>
        </div>
        {needsAttention.length === 0 ? (
          <div style={{ padding: '36px 0', textAlign: 'center', color: '#6E7278', fontSize: 13 }}>
            No SKUs in the reorder window.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {needsAttention.map(m => (
              <NeedsAttentionCard
                key={m.id}
                m={m}
                velocity={velocityFor(weeklyData, m.id)}
                tone={coverTone(m.daysOfCover)}
                onClick={() => openSku(m.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Empty state */}
      {skus.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a1a1aa' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#52525b' }}>No SKU data yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Add SKUs in the Catalogue tab to populate this dashboard.</div>
        </div>
      )}
    </div>
  );
}

function ActionBanner({ alerts }) {
  if (alerts.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 18px',
        background: '#F0F8F4', border: '1px solid #CDE8D7', borderRadius: 10,
        color: '#0F8E6F', fontSize: 13,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>All clear. No stock alerts.</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: SANS, fontSize: 16, fontWeight: 500, color: '#0F1419' }}>Action required</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: '#FEE2E2', color: '#DC2626' }}>{alerts.length}</span>
      </div>
      {alerts.map(a => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: a.priority === 'red' ? '#DC2626' : '#D97706', flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13, color: '#0F1419', lineHeight: 1.4 }}>
            <strong>{a.sku}</strong> <span style={{ color: '#52525b' }}>{a.text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function KpiCard({ label, value, sub, valueColor = '#0F1419' }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #E8E6E0', borderRadius: 12, padding: '18px 20px',
    }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: '#6E7278',
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 600, color: valueColor, marginTop: 8,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
      }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6E7278', marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function NeedsAttentionCard({ m, velocity, tone, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px',
        background: '#FFFFFF', border: '1px solid #E8E6E0', borderRadius: 10,
        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', width: '100%',
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = '#D5D3CD'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#E8E6E0'}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          {m.catCode && (
            <div style={{ fontFamily: MONO, fontSize: 11.5, color: '#6E7278', letterSpacing: '-0.005em', flex: '0 0 auto' }}>
              {m.catCode}
            </div>
          )}
          <div style={{
            fontSize: 14, color: '#0F1419', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{m.catName}</div>
        </div>
        <div style={{ fontSize: 12, color: '#6E7278', marginTop: 4 }}>
          {m.soh.toLocaleString()} on hand
          {m.incomingQty > 0 && ` · ${m.incomingQty.toLocaleString()} incoming`}
          {' · '}{m.dailyAvg > 0 ? `${Math.round(m.dailyAvg * 10) / 10}/day` : '—'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
        <div style={{
          fontFamily: MONO, fontSize: 18, fontWeight: 500,
          color: tone, fontVariantNumeric: 'tabular-nums',
        }}>{m.daysOfCover === 999 ? '∞' : `${m.daysOfCover}d`}</div>
        <div style={{
          fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#6E7278', marginTop: 2,
        }}>cover</div>
      </div>
      <Sparkline data={velocity} stroke={tone} />
    </button>
  );
}
